import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Card, Button, Stack, Typography, CircularProgress, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip, MenuItem, FormControl, InputLabel, Select } from '@mui/material';
// --- AGREGADO: ÍCONO NECESARIO ---
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { RiPencilLine } from 'react-icons/ri';
import { PasoUno } from '../NewCampaign/StepOne';
import { campaignService } from '../../../../services/campaignService';
import { format } from 'date-fns';
import PopupPlantilla from './popupPlantilla';

export default function ActualizarCampaigns() {
  const navigate = useNavigate();
  const { id } = useParams();
  const baseUrl = import.meta.env.BASE_URL || "/";
  const withBase = (path) => `${baseUrl}${path}`.replace(/\/{2,}/g, "/");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [campaignData, setCampaignData] = useState(null);

  // Estados del formulario
  const [nombreCampaign, setNombreCampaign] = useState('');
  const [filtros, setFiltros] = useState([]);
  const [excelFile, setExcelFile] = useState(null);
  const [audienceSource, setAudienceSource] = useState('all');
  const [sendType, setSendType] = useState('after_approval');
  const [sendDate, setSendDate] = useState('');
  const [sendTime, setSendTime] = useState('');
  const [errors, setErrors] = useState({});
  const [selectedPlantilla, setSelectedPlantilla] = useState(null);
  const [openPopupPlantilla, setOpenPopupPlantilla] = useState(false);
  const [editedPlantillas, setEditedPlantillas] = useState([]); // Array de plantillas editadas: [{id, tipo_contenido_multimedia, multimediaFile}]

  // Estados para selección de plantilla (si no existe)
  const [plantillasDisponibles, setPlantillasDisponibles] = useState([]);
  const [nuevaPlantillaId, setNuevaPlantillaId] = useState('');
  const [loadingPlantillas, setLoadingPlantillas] = useState(false);

  // Cargar datos de la campaña
  // Cargar datos de la campaña
  useEffect(() => {
    const loadCampaign = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await campaignService.obtenerCampaign(id);

        if (response.success && response.data) {
          const data = response.data;
          setCampaignData(data);

          // Inicializar estados con los datos de la campaña
          setNombreCampaign(data.nombre || '');

          // Convertir filtros del formato del backend al formato del frontend
          if (data.filtros && Array.isArray(data.filtros)) {
            const transformedFilters = data.filtros.map(f => ({
              type: f.nombre || f.tipo,
              values: f.valores || []
            }));
            setFiltros(transformedFilters);
          }

          // Configurar audiencia
          setAudienceSource(data.upload_audiencia && data.upload_audiencia.length > 0 ? 'excel' : 'all');

          // Configurar fecha y hora de envío si existe
          if (data.fecha_hora_envio) {
            // Parsear fecha_hora_envio del formato "AAAA-MM-DD HH:MM"
            const [datePart, timePart] = data.fecha_hora_envio.split(' ');
            if (datePart && timePart) {
              setSendType('specific_date');
              setSendDate(datePart);
              setSendTime(timePart);
            }
          }

          // Si no tiene plantillas, cargar las disponibles
          if (!data.plantillas || data.plantillas.length === 0) {
            loadPlantillasDisponibles();
          }
        } else {
          setError('No se pudieron cargar los datos de la campaña');
        }
      } catch (err) {
        console.error('Error al cargar campaña:', err);
        setError(err.message || 'Error al cargar los datos de la campaña');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadCampaign();
    }
  }, [id]);

  const loadPlantillasDisponibles = async () => {
    try {
      setLoadingPlantillas(true);
      const response = await campaignService.listarPlantillas();
      if (response.success && response.data) {
        setPlantillasDisponibles(response.data);
      }
    } catch (error) {
      console.error('Error al cargar plantillas:', error);
    } finally {
      setLoadingPlantillas(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validar nombre de campaña
      if (!nombreCampaign.trim()) {
        setErrors({ value: 'El nombre de la campaña es requerido' });
        setSaving(false);
        return;
      }

      // Validar si seleccionó Excel pero no subió archivo
      if (audienceSource === 'excel' && !excelFile && (!campaignData?.upload_audiencia || campaignData.upload_audiencia.length === 0)) {
        setErrors({ excel: 'Debes subir un archivo Excel para continuar con audiencia importada.' });
        setSaving(false);
        return;
      }

      // Validar plantilla si se está asignando una nueva
      if ((!campaignData?.plantillas || campaignData.plantillas.length === 0) && !nuevaPlantillaId) {
        setError('Debes seleccionar una plantilla para la campaña');
        setSaving(false);
        return;
      }

      // Preparar filtros en el formato correcto
      const transformedFilters = filtros.map(f => ({
        nombre: f.type,
        valores: f.values || []
      }));

      // Formatear fecha_hora_envio
      let fechaHoraEnvio = null;
      if (sendType === 'specific_date' && sendDate && sendTime) {
        const [hours, minutes] = sendTime.split(':');
        fechaHoraEnvio = `${sendDate} ${hours}:${minutes}`;
      }

      // Preparar array de plantillas editadas
      const plantillasEditadas = editedPlantillas.length > 0
        ? editedPlantillas.map(p => ({
          id: p.id,
          tipo_contenido_multimedia: p.tipo_contenido_multimedia
        }))
        : [];

      // Preparar datos para enviar
      const updateData = {
        nombre_campaign: nombreCampaign,
        upload_audiencia: audienceSource === 'excel',
        filtros: transformedFilters,
        fecha_hora_envio: fechaHoraEnvio,
        plantillas: plantillasEditadas,
        plantillaId: nuevaPlantillaId ? Number(nuevaPlantillaId) : undefined
      };

      // Obtener archivos multimedia de las plantillas editadas
      const plantillaMultimediaFile = editedPlantillas.find(p => p.multimediaFile)?.multimediaFile || null;

      // Enviar actualización al backend
      await campaignService.actualizarCampaign(
        id,
        updateData,
        excelFile,
        plantillaMultimediaFile
      );

      // Redirigir a la lista de campañas
      navigate(withBase('dashboard/automatizacion/campaigns'));
    } catch (err) {
      console.error('❌ [Actualizar] Error al actualizar campaña:', err);
      setError(err.message || 'Error al actualizar la campaña. Por favor intenta nuevamente.');
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(withBase('dashboard/automatizacion/campaigns'));
  };

  // Función para mapear código de lenguaje del backend al frontend
  const mapLanguageCode = (backendCode) => {
    const languageMap = {
      'en_US': 'en',
      'es_ES': 'es',
      'es_PE': 'esPE'
    };
    return languageMap[backendCode] || backendCode;
  };

  // Función para formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch (error) {
      return dateString;
    }
  };

  // Función para obtener el nombre del canal
  const getChannelName = (codigoCanal) => {
    const channelMap = {
      'whatsapp': 'WhatsApp',
      'phone': 'Teléfono'
    };
    return channelMap[codigoCanal] || codigoCanal;
  };

  // Función para obtener el nombre del tipo de contenido
  const getContentTypeName = (tipoContenido) => {
    if (!tipoContenido) return 'N/A';
    const typeMap = {
      'imagen': 'Imagen',
      'video': 'Video',
      'audio': 'Audio',
      'documento': 'Documento'
    };
    return typeMap[tipoContenido.toLowerCase()] || tipoContenido;
  };

  // Función para manejar editar plantilla
  const handleEditPlantilla = (plantillaId) => {
    console.log('🔍 [Actualizar] handleEditPlantilla llamado con ID:', plantillaId);
    console.log('🔍 [Actualizar] campaignData:', campaignData);
    console.log('🔍 [Actualizar] plantillas disponibles:', campaignData?.plantillas);

    const plantilla = campaignData?.plantillas?.find(p => p.id === plantillaId);
    console.log('🔍 [Actualizar] plantilla encontrada:', plantilla);

    if (plantilla) {
      setSelectedPlantilla(plantilla);
      setOpenPopupPlantilla(true);
      console.log('✅ [Actualizar] Popup abierto, selectedPlantilla:', plantilla);
    } else {
      console.error('❌ [Actualizar] No se encontró la plantilla con ID:', plantillaId);
    }
  };

  // Función para guardar cambios de la plantilla
  const handleSavePlantilla = async (newMultimediaFile) => {
    if (!selectedPlantilla) return;

    try {
      console.log('💾 [Actualizar] Guardar plantilla:', selectedPlantilla.id, 'Nuevo archivo:', newMultimediaFile);

      // Solo guardar si hay un archivo nuevo subido
      if (newMultimediaFile) {
        // Guardar información de la plantilla editada
        const plantillaEditada = {
          id: selectedPlantilla.id,
          tipo_contenido_multimedia: selectedPlantilla.tipo_contenido_multimedia || '',
          multimediaFile: newMultimediaFile
        };

        // Actualizar el array de plantillas editadas
        setEditedPlantillas(prev => {
          // Remover la plantilla si ya existe (para actualizarla)
          const filtered = prev.filter(p => p.id !== selectedPlantilla.id);
          // Agregar la nueva información
          return [...filtered, plantillaEditada];
        });

        console.log('✅ [Actualizar] Plantilla editada guardada:', plantillaEditada);
      } else {
        console.log('ℹ️ [Actualizar] No se subió archivo nuevo, no se guarda como editada');
      }

      // Cerrar el popup
      setOpenPopupPlantilla(false);
      setSelectedPlantilla(null);
    } catch (error) {
      console.error('❌ [Actualizar] Error al guardar plantilla:', error);
      setError('Error al guardar los cambios de la plantilla');
    }
  };

  // Preparar datos iniciales para StepOne
  const initialData = campaignData ? {
    nombre_campaign: campaignData.nombre,
    filtros: campaignData.filtros || [],
    upload_audiencia: campaignData.upload_audiencia, // Pasar el array completo con el link si existe
    sendType: sendType,
    sendDate: sendDate,
    sendTime: sendTime
  } : null;

  if (loading) {
    return (
      <Box sx={{ background: "#f7f8fc", minHeight: "100vh", p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Cargando datos de la campaña...</Typography>
        </Box>
      </Box>
    );
  }

  if (error && !campaignData) {
    return (
      <Box sx={{ background: "#f7f8fc", minHeight: "100vh", p: 4 }}>
        <Card sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              backgroundColor: '#6a8bff',
              color: '#fff',
              textTransform: 'none',
            }}
          >
            Volver a la lista
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ background: "#f7f8fc", minHeight: "100vh", p: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: "bold", color: "#031b4e", fontSize: 24 }}>
          Editar Campaña
        </Typography>
        <Typography sx={{ color: "#6B7280", fontSize: 14, mt: 1 }}>
          Modifica los datos de tu campaña
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <PasoUno
        value={nombreCampaign}
        setValue={setNombreCampaign}
        filter={filtros}
        setFilter={setFiltros}
        errors={errors}
        setErrors={setErrors}
        handleNext={(value, filter, excelFile, audienceSource, sendType, sendDate, sendTime) => {
          // Actualizar estados cuando se llama handleNext desde StepOne
          setNombreCampaign(value);
          setFiltros(filter);
          setExcelFile(excelFile);
          setAudienceSource(audienceSource);
          setSendType(sendType);
          setSendDate(sendDate);
          setSendTime(sendTime);
        }}
        initialData={initialData}
        hideContinueButton={true}
        campaignId={id}
      />

      {/* ===================================================================== */}
      {/* AGREGADO: BOTÓN DESCARGAR AUDIENCIA (SIEMPRE VISIBLE)                 */}
      {/* ===================================================================== */}
      {/* <Box sx={{ mt: 3, mb: 4, ml: 1, display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          variant="outlined"
          startIcon={<InsertDriveFileIcon />}
          onClick={() => {
            const link = document.createElement('a');
            // Usamos ruta relativa al dominio actual
            link.href = `/api/campania/${id}/descargar-audiencia`;
            link.download = `audiencia_campania_${id}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
          sx={{
            borderColor: '#25d366',
            color: '#25d366',
            textTransform: 'none',
            fontSize: 14,
            fontWeight: 600,
            padding: '10px 24px',
            borderRadius: 2,
            backgroundColor: '#fff',
            borderWidth: '1px',
            '&:hover': {
              borderColor: '#20ba5a',
              background: '#f0fdf4',
              borderWidth: '1px',
            },
          }}
        >
          Descargar audiencia actual (.xlsx)
        </Button>
      </Box> */}

      {/* Sección de Plantillas */}
      {campaignData?.plantillas && campaignData.plantillas.length > 0 && (
        <Card sx={{ mt: 4, p: 3 }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: 20, color: '#031b4e', mb: 3 }}>
            Plantilla
          </Typography>
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E5E7EB' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f7f8fc' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#4B5563', fontSize: 14 }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#4B5563', fontSize: 14 }}>Tipo de contenido</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#4B5563', fontSize: 14 }}>Estado Meta</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#4B5563', fontSize: 14 }}>Canal</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#4B5563', fontSize: 14 }}>Fecha de creación</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#4B5563', fontSize: 14 }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaignData.plantillas.map((plantilla) => (
                  <TableRow key={plantilla.id} sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                    <TableCell sx={{ fontSize: 14, color: '#111827' }}>
                      {plantilla.nombre || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 14, color: '#111827' }}>
                      {getContentTypeName(plantilla.tipo_contenido_multimedia)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 14, color: '#111827' }}>
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: 12,
                          fontWeight: 500,
                          backgroundColor:
                            plantilla.estado === 'APPROVED'
                              ? '#D1FAE5'
                              : plantilla.estado === 'PENDING'
                                ? '#FEF3C7'
                                : '#FEE2E2',
                          color:
                            plantilla.estado === 'APPROVED'
                              ? '#065F46'
                              : plantilla.estado === 'PENDING'
                                ? '#92400E'
                                : '#991B1B',
                        }}
                      >
                        {plantilla.estado || 'N/A'}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: 14, color: '#111827' }}>
                      {getChannelName(campaignData.codigo_canal)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 14, color: '#111827' }}>
                      {formatDate(plantilla.created_at)}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Editar plantilla">
                        <IconButton
                          size="small"
                          onClick={() => handleEditPlantilla(plantilla.id)}
                          sx={{
                            color: '#6a8bff',
                            '&:hover': {
                              backgroundColor: '#f5f8ff',
                              color: '#5a7aef',
                            },
                          }}
                        >
                          <RiPencilLine size={18} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Stack direction='row' justifyContent='flex-end' spacing={2} sx={{ mt: 3, mb: 1 }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          disabled={saving}
          sx={{
            borderColor: '#6a8bff',
            color: '#6a8bff',
            textTransform: 'none',
            fontSize: 14,
            borderRadius: 2,
            paddingX: 4,
            paddingY: 1.5,
            fontWeight: 500,
            '&:hover': {
              borderColor: '#5a7aef',
              background: '#f5f8ff',
            },
          }}
        >
          Retroceder
        </Button>
        <Button
          variant="contained"
          onClick={handleUpdate}
          disabled={saving}
          sx={{
            backgroundColor: '#6a8bff',
            color: '#fff',
            fontSize: 14,
            borderRadius: 2,
            boxShadow: 'none',
            textTransform: 'none',
            paddingX: 4,
            paddingY: 1.5,
            fontWeight: 500,
            '&:hover': {
              backgroundColor: '#5a7aef',
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: '#ccc',
            },
          }}
        >
          {saving ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
              Actualizando...
            </>
          ) : (
            'Actualizar'
          )}
        </Button>
      </Stack>

      {/* Popup para editar plantilla */}
      {console.log('🔍 [Actualizar] Renderizando PopupPlantilla:', {
        openPopupPlantilla,
        hasSelectedPlantilla: !!selectedPlantilla,
        selectedPlantilla
      })}
      <PopupPlantilla
        open={openPopupPlantilla}
        onClose={() => {
          console.log('🔍 [Actualizar] Cerrando popup');
          setOpenPopupPlantilla(false);
          setSelectedPlantilla(null);
        }}
        plantilla={selectedPlantilla}
        onSave={handleSavePlantilla}
      />
    </Box>
  );
}
